import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import LoadingSpinner from './LoadingSpinner';

const StyledButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ size, theme }) => {
    switch (size) {
      case 'sm': return `${theme.spacing.sm} ${theme.spacing.md}`;
      case 'lg': return `${theme.spacing.md} ${theme.spacing.xl}`;
      default: return `${theme.spacing.sm} ${theme.spacing.lg}`;
    }
  }};
  background: ${({ variant, theme }) => {
    switch (variant) {
      case 'primary': return theme.colors.primary[500];
      case 'secondary': return theme.colors.dark.elevated;
      case 'danger': return theme.colors.accent.red;
      case 'ghost': return 'transparent';
      default: return theme.colors.primary[500];
    }
  }};
  border: ${({ variant, theme }) => {
    switch (variant) {
      case 'ghost': return `1px solid ${theme.colors.dark.border}`;
      case 'secondary': return `1px solid ${theme.colors.dark.border}`;
      default: return 'none';
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ variant, theme }) => {
    switch (variant) {
      case 'secondary': return theme.colors.dark.text;
      case 'ghost': return theme.colors.dark.textSecondary;
      default: return 'white';
    }
  }};
  font-size: ${({ size }) => {
    switch (size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1.125rem';
      default: return '1rem';
    }
  }};
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  min-height: ${({ size }) => {
    switch (size) {
      case 'sm': return '32px';
      case 'lg': return '48px';
      default: return '40px';
    }
  }};
  
  &:hover:not(:disabled) {
    background: ${({ variant, theme }) => {
      switch (variant) {
        case 'primary': return theme.colors.primary[600];
        case 'secondary': return theme.colors.dark.surface;
        case 'danger': return theme.colors.accent.red;
        case 'ghost': return theme.colors.dark.elevated;
        default: return theme.colors.primary[600];
      }
    }};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: ${({ size }) => {
      switch (size) {
        case 'sm': return '14px';
        case 'lg': return '20px';
        default: return '16px';
      }
    }};
    height: ${({ size }) => {
      switch (size) {
        case 'sm': return '14px';
        case 'lg': return '20px';
        default: return '16px';
      }
    }};
  }
`;

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  onClick,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      disabled={disabled || loading}
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      {...props}
    >
      {loading && <LoadingSpinner size={size === 'sm' ? 14 : size === 'lg' ? 20 : 16} />}
      {children}
    </StyledButton>
  );
};

export default Button;
